import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import * as catalogApi from '../api/endpoints/catalog';
import { visibleCategories } from '../utils/categories';
import { focusRing, homeRail } from '../styles/ui';

// Each banner is bound to a real admin category key (see
// freshnhomely-backend/src/utils/categorySlugs.js). `keys` is tried in order so
// a store that still serves the pre-migration slug keeps working; a banner
// whose category the API does not return is simply not rendered, rather than
// linking somewhere that 404s. Artwork is the approved design's own, cropped
// into /home/promo-*.webp.
const BANNERS = [
  {
    id: 'produce',
    keys: ['fresh-fruits-and-vegetables'],
    title: ['Fresh', 'Vegetables & Fruits'],
    subtitle: 'Naturally grown | Rich in nutrition',
    image: '/home/promo-produce.webp',
    surface: 'bg-[#E9F5E4]',
    heading: 'text-[#1E5A30]',
    button: 'bg-[#2E8B47] text-white',
  },
  {
    id: 'meat',
    keys: ['chicken', 'goat', 'mutton-goat-lamb'],
    title: ['Premium', 'Meat & Poultry'],
    subtitle: 'Hygienic | Fresh Cuts | Trusted Quality',
    image: '/home/promo-meat.webp',
    surface: 'bg-[#FCE9EC]',
    heading: 'text-[#C0284B]',
    button: 'bg-[#E0245E] text-white',
  },
  {
    id: 'seafood',
    keys: ['seafood'],
    title: ['Fresh', 'Seafood'],
    subtitle: ['Straight from the source', 'Cleaned & Packed'],
    image: '/home/promo-seafood.webp',
    surface: 'bg-[#E4F0FA]',
    heading: 'text-[#1D5B8C]',
    button: 'bg-[#1F6FB5] text-white',
  },
];

const PromoBanners = () => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let active = true;
    catalogApi
      .listCategories()
      .then((response) => {
        if (active) setCategories(visibleCategories(response.data));
      })
      .catch(() => {
        if (active) setCategories([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const available = new Set(categories.map((category) => category.key));
  const banners = BANNERS.map((banner) => ({
    ...banner,
    key: banner.keys.find((key) => available.has(key)),
  })).filter((banner) => banner.key);

  if (banners.length === 0) return null;

  return (
    <section aria-label="Featured departments" className="pt-3 lg:pt-4 bg-white">
      <div className={homeRail}>
        <ul className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {banners.map((banner) => {
            const subtitle = Array.isArray(banner.subtitle) ? banner.subtitle : [banner.subtitle];
            return (
              <li key={banner.id}>
                <Link
                  to={`/categories/${banner.key}`}
                  className={`group relative flex items-center overflow-hidden rounded-2xl ${banner.surface} h-[142px] ${focusRing}`}
                >
                  <div className="relative z-10 pl-6 xl:pl-9 pr-3 w-[60%]">
                    <h3 className={`serif text-[22px] xl:text-[24px] font-bold leading-[1.1] ${banner.heading}`}>
                      {banner.title[0]}
                      <br />
                      {banner.title[1]}
                    </h3>
                    <p className="text-[12px] text-[#4B5563] mt-[6px] leading-[1.25]">
                      {subtitle.map((line, index) => (
                        <React.Fragment key={line}>
                          {index > 0 && <br />}
                          {line}
                        </React.Fragment>
                      ))}
                    </p>
                    <span
                      className={`mt-[9px] inline-flex items-center gap-1.5 h-[26px] px-4 rounded-full text-[11.5px] font-bold shadow-[0_3px_8px_-2px_rgba(15,51,24,0.3)] transition-transform group-hover:translate-x-0.5 ${banner.button}`}
                    >
                      Shop Now
                      <ArrowRight size={12} strokeWidth={2.5} aria-hidden="true" />
                    </span>
                  </div>

                  <img
                    src={banner.image}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="pointer-events-none absolute right-0 top-0 h-full w-auto max-w-[66%] object-cover object-right"
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default PromoBanners;
